/* eslint-disable @next/next/no-img-element */
import styled from '@emotion/styled';
import { forwardRef } from 'react';
import PropTypes from 'prop-types';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import H3 from '@base/H3/H3';
import { Spacings, mediaQuery, Fonts } from '@styles/index';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import breadcrumbs, { goBack } from '@utils/breadcrumbs';
import { getBatchByUID } from '@selectors/batchGroups';
import { useSelectorToStore } from '@hooks/reduxHooks';
import Button from '@base/Button/Button';
import H7 from '@base/H7/H7';
import { H2, H4 } from '@styles/constants/fonts';
import usePlatformEventProps from '@hooks/usePlatformBatchEventProps';
import { segment } from '@actions/analytics';
import { PLATFORM_BATCH_CHECK_STARTED } from '@constants/segment';
import pushRoute from '@utils/pushRoute';
import Divider from '@base/Divider/Divider';
import BatchState from '@cont/Batch/PlatformBatchSmallHeader';
import { getIsSubscribedToOffline } from '@api/selectors/subscription';
import useMyInfo from '@api/hooks/auth/useMyInfo';

import BatchThumbnail from './BatchThumbNail';
import BatchShare from './BatchShare';
import {
  getIsEnrollmentClosed,
  getJoinBatchLabel,
  getSlashPriceMessage
} from './utils';
import BatchStatus from './BatchStatus';
import GreyOutPriceSection from './GreyOutPrice';
import BatchGroupTags from './BatchGroupTags';
import BatchGroupCardInfo from './BatchGroupCardInfo';

const Wrapper = styled.div`
  grid-column: span 24;
  margin-bottom: ${Spacings.SPACING_8B};
  background: var(--color-base-1);
  padding: ${({ $isStickyCard }) => {
    return $isStickyCard ? Spacings.SPACING_6B : Spacings.SPACING_0B;
  }};
  box-shadow: ${({ $isStickyCard }) => {
    return $isStickyCard ? boxShadowGenerator() : 'none';
  }};
  border-radius: ${({ $isStickyCard }) => {
    return $isStickyCard ? Spacings.SPACING_2B : Spacings.SPACING_0B;
  }};
  margin-top: ${({ $isStickyCard }) => {
    return $isStickyCard ? Spacings.SPACING_0B : Spacings.SPACING_6B;
  }};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_0B};
    margin-top: ${Spacings.SPACING_0B};
  }
`;

const Header = styled.div`
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  transition: all 300ms ease;
  padding-top: ${({ $isStickyCard }) => {
    return $isStickyCard ? Spacings.SPACING_0B : Spacings.SPACING_8B;
  }};
  ${mediaQuery.uptoMobile} {
    box-shadow: none;
    padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_0B}
      ${Spacings.SPACING_0B};
  }
`;

const BreadCrumb = styled(BreadCrumbs)`
  ${(scroll) =>
    scroll
      ? `height: ${Spacings.SPACING_0B};
        overflow: hidden;
        opacity: 0;`
      : 'opacity: 1;'}
  transition: all 300ms ease;
  & > * {
    box-shadow: ${boxShadowGenerator({
      yOffset: Spacings.SPACING_1B,
      blur: Spacings.SPACING_4B
    })};
  }
`;

const TopBar = styled.div`
  margin-bottom: ${Spacings.SPACING_10B};
  transition: all 300ms ease;
  ${mediaQuery.uptoMobile} {
    display: none;
  }
`;

const CourseInfo = styled.div`
  flex: 1;
  margin-right: ${({ $isStickyCard }) => {
    return $isStickyCard ? Spacings.SPACING_0B : Spacings.SPACING_15B;
  }};
  ${mediaQuery.uptoMobile} {
    margin-top: ${Spacings.SPACING_6B};
    margin-right: ${Spacings.SPACING_0B};
  }
`;

const CourseTitle = styled(H3)`
  transition: all 300ms ease;
  color: var(--color-text-primary);
  margin-bottom: ${Spacings.SPACING_4B};
  font-size: ${({ $isStickyCard }) => {
    return $isStickyCard ? H4 : H2;
  }};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin: ${Spacings.SPACING_1B} 0 ${Spacings.SPACING_3B};
  }
`;

const HeaderContentLeft = styled.div`
  margin-top: ${({ $isStickyCard }) => {
    return $isStickyCard ? Spacings.SPACING_1B : Spacings.SPACING_2B;
  }};
  padding-right: ${Spacings.SPACING_0B};
`;

const HeaderContentRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  margin-top: ${Spacings.SPACING_5B};
  ${mediaQuery.uptoMobile} {
    width: 100%;
    flex-direction: row;
    > button:last-child {
      margin-right: ${Spacings.SPACING_2B};
    }
  }
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  ${mediaQuery.uptoMobile} {
    flex-direction: column-reverse;
  }
`;
const BatchInfo = styled(Flex)`
  flex-direction: column;
  ${mediaQuery.uptoMobile} {
    flex-direction: column;
  }
`;

const Price = styled.div`
  display: flex;
  align-items: flex-end;
`;
const SlashPrice = styled.div`
  text-decoration-line: line-through;
  margin-right: ${Spacings.SPACING_2B};
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.H3};
  color: var(--color-text-secondary);
`;

const AssociationWith = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacings.SPACING_0B};
`;
const AssociationLogo = styled.div`
  margin-top: ${Spacings.SPACING_2B};
  background: var(--color-i-white);
  width: fit-content;
  height: 24px;
`;

const JoinBatchButton = styled(Button)`
  background-color: var(--color-base-fill);
  color: var(--color-text-tertiary);
  width: ${({ $isStickyCard }) => {
    return $isStickyCard ? '100%' : '268px';
  }};
  &:active {
    background-color: var(--color-i-black);
  }
  ${mediaQuery.uptoMobile} {
    font-size: ${Fonts.SMALL};
    width: 100%;
  }
`;

const ScrollFooter = styled.div`
  display: none;
  ${mediaQuery.uptoMobile} {
    display: block;
  }
`;
const StyledDivider = styled(Divider)`
  margin: ${Spacings.SPACING_4B} auto ${Spacings.SPACING_4B};
  width: 100%;
`;

const BatchCardInfoContainer = styled.div`
  min-height: ${({ $isStickyCard }) => {
    return $isStickyCard ? '100%' : 'unset';
  }};
  margin-top: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    min-height: 100%;
  }
`;

const SlashPriceMessage = styled.div`
  font-weight: ${Fonts.LIGHT};
  font-size: ${Fonts.SMALL};
  color: var(--color-text-primary);
  padding-bottom: ${Spacings.SPACING_1B};
`;
const PriceAndSlashMessageWrapper = styled.div`
  margin-top: ${({ $isStickyCard }) => {
    return $isStickyCard ? Spacings.SPACING_0B : Spacings.SPACING_8B;
  }};
`;

const CourseByText = styled(H7)`
  color: var(--color-text-secondary);
`;

const PlatformBatchHeader = forwardRef(
  ({
    batchGroupUID,
    scroll,
    isStickyCard,
    lastPrimarySourceSection,
    entitySubType
  }) => {
    const batch = useSelectorToStore(getBatchByUID(batchGroupUID));
    const segmentData = usePlatformEventProps({
      batch,
      isBatch: true,
      entityContentType: true,
      lastPrimarySourceSection,
      isPurchaseType: true,
      isSlashedPriceType: true
    });
    const batchGoalUID = batch?.goal?.uid;
    const isOfflineSubscription = useMyInfo(
      getIsSubscribedToOffline(batchGoalUID)
    );
    if (!batch) return null;
    const {
      name,
      goal = {},
      uid,
      partnerLog,
      price,
      enrollmentEndDate,
      languages,
      subscriptionUID,
      batchUID,
      isEnrolled,
      isDiscountAvailable,
      discountEndDate,
      listPrice,
      promoText,
      classType,
      stateMetaInfo
    } = batch;
    const onJoinBatch = () => {
      segmentData.entity_uid = batchUID;
      segmentData.entity_title = name;
      segmentData.entity_sub_type = entitySubType;
      segment.track(PLATFORM_BATCH_CHECK_STARTED, segmentData);
      pushRoute(
        '/batch-group/[slug]/subscribe/[subscriptionUID]',
        `/batch-group/${batchUID}/subscribe/${subscriptionUID}`
      );
    };
    const isEnrollmentClose = getIsEnrollmentClosed(enrollmentEndDate);
    const hidePriceAndJoinButton = isEnrollmentClose || isEnrolled;
    const showDivider = isStickyCard && !hidePriceAndJoinButton;
    const slashPriceMessage = getSlashPriceMessage(
      discountEndDate,
      promoText,
      isDiscountAvailable
    );

    const showSlashMessage =
      !isEnrolled && !isEnrollmentClose && slashPriceMessage;
    const isNotifyingBannerVisible = isEnrollmentClose && !isEnrolled;
    const joinBatchLabel = getJoinBatchLabel(classType);
    return (
      <Wrapper $isStickyCard={isStickyCard}>
        <ScrollFooter>
          <BatchState
            scroll={scroll}
            batchUID={uid}
            onJoinBatch={onJoinBatch}
            slashPriceMessage={slashPriceMessage}
            batch={batch}
          />
        </ScrollFooter>
        <Header $isStickyCard={isStickyCard} scroll={scroll}>
          {!isStickyCard && (
            <TopBar>
              <BreadCrumb
                onBackClick={goBack}
                tags={breadcrumbs.getBatchTags(goal, isOfflineSubscription)}
              />
            </TopBar>
          )}

          <Flex>
            <CourseInfo $isStickyCard={isStickyCard}>
              <BatchInfo>
                <>
                  <BatchGroupTags
                    languages={languages}
                    classType={classType}
                    tagsBGColor="transparent"
                    hasBorder
                  />
                </>
                <HeaderContentLeft $isStickyCard={isStickyCard}>
                  <CourseTitle
                    $isStickyCard={isStickyCard}
                    lineClamp={0}
                    as="h1"
                  >
                    {name}
                  </CourseTitle>

                  {partnerLog && !isStickyCard && (
                    <AssociationWith>
                      <CourseByText>Batch by</CourseByText>
                      <AssociationLogo>
                        <img src={partnerLog} alt="logo" height="100%" />
                      </AssociationLogo>
                    </AssociationWith>
                  )}

                  <BatchCardInfoContainer $isStickyCard={isStickyCard}>
                    <BatchGroupCardInfo batch={batch} height={24} width={24} />
                  </BatchCardInfoContainer>
                  {showDivider && <StyledDivider />}
                  {isNotifyingBannerVisible && !isStickyCard ? (
                    <GreyOutPriceSection price={price} />
                  ) : (
                    <PriceAndSlashMessage
                      isStickyCard={isStickyCard}
                      showSlashMessage={showSlashMessage}
                      slashPriceMessage={slashPriceMessage}
                      hidePriceAndJoinButton={hidePriceAndJoinButton}
                      isDiscountAvailable={isDiscountAvailable}
                      listPrice={listPrice}
                      price={price}
                    />
                  )}
                </HeaderContentLeft>

                {!hidePriceAndJoinButton && (
                  <HeaderContentRight>
                    <JoinBatchButton
                      label={joinBatchLabel}
                      batchUID={batchGroupUID}
                      onClick={onJoinBatch}
                      variant="filled"
                      $isStickyCard={isStickyCard}
                    />
                    {!isStickyCard && <BatchShare batchUID={batchGroupUID} />}
                  </HeaderContentRight>
                )}
                <BatchStatus
                  stateMetaInfo={stateMetaInfo}
                  isEnrolled={isEnrolled}
                />
              </BatchInfo>
            </CourseInfo>
            {!isStickyCard && <BatchThumbnail batchUID={uid} />}
          </Flex>
        </Header>
      </Wrapper>
    );
  }
);

const PriceAndSlashMessage = ({
  showSlashMessage,
  slashPriceMessage,
  hidePriceAndJoinButton,
  isDiscountAvailable,
  listPrice,
  price,
  isStickyCard
}) => {
  return (
    <PriceAndSlashMessageWrapper $isStickyCard={isStickyCard}>
      {showSlashMessage && (
        <SlashPriceMessage>{slashPriceMessage}</SlashPriceMessage>
      )}
      {!hidePriceAndJoinButton && (
        <Price>
          {isDiscountAvailable && listPrice && (
            <SlashPrice>₹{listPrice}</SlashPrice>
          )}
          <H3>₹{price} </H3>
        </Price>
      )}
    </PriceAndSlashMessageWrapper>
  );
};

PlatformBatchHeader.displayName = 'PlatformBatchHeader';

export default PlatformBatchHeader;

PlatformBatchHeader.propTypes = {
  scroll: PropTypes.bool.isRequired,
  batchGroupUID: PropTypes.string.isRequired
};
